import { useCallback, useEffect } from 'react'
import type { JSX } from 'react'

import {
  Button,
  Card,
  ChannelIcon,
  CheckIcon,
  ChevronDownIcon,
  Dialog,
  DropdownMenu,
  Flex,
  Heading,
  Link,
  List,
  Notice,
  Strong,
  Text,
  ExternalLinkIcon,
} from '@buffer-mono/popcorn'

import { Header } from '../Header/Header'
import { Footer } from '../Footer/Footer'
import useAuthorizationInfo from '../../../../hooks/useAuthorizationInfo/useAuthorizationInfo'
import { InstagramAuthMethod, Service } from '../../../../types'
import type { OnRedirectParams } from '../../../../types'

import styles from './FacebookAuthConfirmation.module.css'
import footerStyles from '../Footer/Footer.module.css'

const LINK_INSTAGRAM_TO_FACEBOOK_GUIDE =
  'https://support.buffer.com/en-us/articles/connecting-your-instagram-account-to-buffer-n9Ad6veXsu?utm_source=buffer&utm_medium=learn-more-link&utm_campaign=learn-more#h-connecting-a-professional-instagram-business-or-creator-account-through-a-linked-facebook-page'

const TROUBLESHOOTING_GUIDE =
  'https://support.buffer.com/articles/getting-instagram-advanced-analytics-and-locations-facebook-authorization-required-acaC8V633l?utm_source=buffer&utm_medium=learn-more-link&utm_campaign=learn-more'

export function FacebookAuthConfirmation({
  onBack,
  onRedirect,
}: {
  onBack: () => void
  onRedirect: (args: OnRedirectParams) => void
}): JSX.Element {
  const {
    handleGetAuthorizationInfo,
    url,
    isAuthorizationInfoLoading,
    authorizationInfoErrorMessage,
  } = useAuthorizationInfo()

  const requestAuthorizationInfo = useCallback((): void => {
    void handleGetAuthorizationInfo({
      service: Service.instagram,
      authMethod: InstagramAuthMethod.facebookLogin,
      isRefreshingConnection: false,
    })
  }, [handleGetAuthorizationInfo])

  useEffect(() => {
    requestAuthorizationInfo()
  }, [requestAuthorizationInfo])

  return (
    <>
      <Header onBack={onBack} />

      <Dialog.Body className={styles.body}>
        <div className={styles.titleSubtitle}>
          <Heading size="lg" align="center">
            Connect Instagram through Facebook
          </Heading>
          <Text align="center" className={styles.subtitle}>
            Here&apos;s what you need to know 👇
          </Text>
        </div>

        <Flex justify="center">
          <Card className={styles.card}>
            <Flex gap="xs" align="center" className={styles.cardHeading}>
              <Flex align="center" className={styles.channelIcons}>
                <ChannelIcon type="instagram" color="branded" size="small" />
                <Text weight="bold">+</Text>
                <ChannelIcon type="facebook" color="branded" size="small" />
              </Flex>
              <Heading as="h3" size="sm">
                Requirements
              </Heading>
            </Flex>

            <List className={styles.requirementsList}>
              <List.Item icon={CheckIcon}>
                <Text>
                  <Strong>Business or Creator</Strong> Instagram, not a Personal
                  Instagram profile.
                </Text>
              </List.Item>
              <List.Item icon={CheckIcon}>
                <Text>
                  <Strong>Linked to a Facebook page</Strong>, not a Facebook
                  profile. Need to link Instagram to Facebook on Meta?{' '}
                  <Link href={LINK_INSTAGRAM_TO_FACEBOOK_GUIDE} external>
                    Learn how.
                  </Link>
                </Text>
              </List.Item>
              <List.Item icon={CheckIcon}>
                <Text>
                  <Strong>Logged in as Facebook Page admin</Strong> with
                  &ldquo;full control&rdquo;.
                </Text>
              </List.Item>
              <List.Item icon={CheckIcon}>
                <Text>
                  <Strong>
                    All permissions selected for all Pages and Instagram
                    accounts
                  </Strong>{' '}
                  when connecting, even ones you won&apos;t connect to Buffer.
                </Text>
              </List.Item>
            </List>

            <Dialog.Separator className={styles.separator} />
            <Text size="sm" color="subtle">
              The connection won&apos;t work if any of these requirements
              aren&apos;t met.
            </Text>
          </Card>
        </Flex>

        {authorizationInfoErrorMessage && (
          <Flex justify="center">
            <Notice variant="error" className={styles.errorNotice}>
              <Notice.Heading>
                We couldn&apos;t start Facebook Login.
              </Notice.Heading>
              <Notice.Text>
                Try again. If the problem continues, use the Help menu.
              </Notice.Text>
            </Notice>
          </Flex>
        )}
      </Dialog.Body>

      <Footer>
        <DropdownMenu
          align="start"
          className={footerStyles.menu}
          trigger={
            <Button
              variant="tertiary"
              size="large"
              className={footerStyles.helpButton}
            >
              Help
              <ChevronDownIcon />
            </Button>
          }
        >
          <DropdownMenu.Item asChild>
            <a
              className={footerStyles.helpLink}
              href={TROUBLESHOOTING_GUIDE}
              target="_blank"
              rel="noopener noreferrer"
            >
              Troubleshooting and how to link Instagram to a Facebook Page
              <ExternalLinkIcon />
            </a>
          </DropdownMenu.Item>
        </DropdownMenu>

        <Button
          size="large"
          variant="primary"
          disabled={
            isAuthorizationInfoLoading ||
            (!authorizationInfoErrorMessage && !url)
          }
          loading={isAuthorizationInfoLoading}
          onClick={(): void => {
            if (authorizationInfoErrorMessage) {
              requestAuthorizationInfo()
              return
            }

            if (url) {
              onRedirect({
                redirectUrl: url,
                authMethod: InstagramAuthMethod.facebookLogin,
              })
            }
          }}
        >
          {authorizationInfoErrorMessage
            ? 'Try again'
            : 'Connect through Facebook'}
        </Button>
      </Footer>
    </>
  )
}
